import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { formatDMY } from "@/lib/dateFormat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, History } from "lucide-react";
import { toast } from "sonner";

const money = (v) => (v == null ? "—" : `$ ${Number(v).toFixed(2)}`);

export default function ChangePlanDialog({ open, onOpenChange, enrollment, program, studentName, onSaved }) {
  const { t } = useI18n();
  const [snap, setSnap] = useState(null);
  const [sessions, setSessions] = useState("");
  const [price, setPrice] = useState("");
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  const curSessions = enrollment?.classes_included || program?.classes_included || 0;
  const curPrice = enrollment?.amount != null ? enrollment.amount : program?.price;
  const history = enrollment?.plan_changes || [];

  useEffect(() => {
    if (!open || !enrollment?.id) return;
    setSessions(String(curSessions || ""));
    setPrice(curPrice != null ? String(curPrice) : "");
    setNotify(false);
    setSnap(null);
    api.get(`/enrollments/${enrollment.id}/consumption`).then((r) => setSnap(r.data)).catch(() => setSnap(null));
  }, [open, enrollment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      setSaving(true);
      const r = await api.post(`/enrollments/${enrollment.id}/change-plan`, {
        classes_included: Number(sessions),
        amount: price !== "" ? Number(price) : null,
        notify,
      });
      toast.success(t("plan_changed_ok"));
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const nextCycle = (snap?.cycle_number || 1) + 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-plan-dialog">
        <DialogHeader>
          <DialogTitle>{t("change_plan")}</DialogTitle>
          <DialogDescription>{studentName} · {program?.name}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="change-plan-current">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{t("current_plan")}</div>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono font-semibold text-slate-900">{curSessions} <span className="text-[10px] text-slate-500 font-sans">{t("sessions_unit")}</span></span>
            <span className="font-mono font-semibold text-slate-900">{money(curPrice)} <span className="text-[10px] text-slate-500 font-sans">USD</span></span>
          </div>
          {snap && (
            <p className="text-xs text-slate-600 mt-1.5" data-testid="change-plan-progress">
              {t("cycle_in_progress", { n: snap.cycle_number || 1, x: snap.consumed, y: snap.total })}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("new_sessions")}</Label>
            <Input type="number" min="1" step="1" value={sessions} onChange={(e) => setSessions(e.target.value)}
              className="mt-1.5" data-testid="change-plan-sessions-input" />
          </div>
          <div>
            <Label className="text-xs">{t("new_price")}</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
              <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                className="pl-7" data-testid="change-plan-price-input" />
            </div>
          </div>
        </div>
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="change-plan-hint">
          {t("change_plan_hint", { n: nextCycle })}
        </p>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="change-plan-notify-checkbox" />
          {t("notify_student_plan")}
        </label>

        {history.length > 0 && (
          <div className="border-t pt-3" data-testid="change-plan-history">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
              <History className="h-3 w-3" /> {t("plan_history")}
            </div>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto">
              {[...history].reverse().map((h) => (
                <li key={h.id} className="text-xs text-slate-700 flex flex-wrap items-center gap-x-1.5" data-testid={`plan-history-${h.id}`}>
                  <span className="text-slate-400">{formatDMY((h.changed_at || "").slice(0, 10))}</span>
                  <span className="font-mono">{h.from_classes}s · {money(h.from_amount)}</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                  <span className="font-mono font-semibold">{h.to_classes}s · {money(h.to_amount)}</span>
                  <span className="text-slate-500">({t("from_cycle", { n: h.cycle_from })}{h.notified ? ` · ${t("notified")}` : ""})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="change-plan-cancel-btn">{t("cancel")}</Button>
          <Button onClick={submit} disabled={saving || !Number(sessions)} data-testid="change-plan-submit-btn">
            {saving ? t("saving") : t("apply_change")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
